import { createFileRoute, redirect } from '@tanstack/react-router'
import { ProfileHero } from './-components/ProfileHero'
import { PersonalInfoForm } from './-components/PersonalInfoForm'
import { UpdatePassword } from './-components/UpdatePassword'
import { BrowserSessions } from './-components/BrowserSessions'
import { ImageCropDialog } from './-components/ImageCropDialog'
import { useProfilePage } from './-hooks/useProfilePage'
import { Fade } from '@/components/animate-ui/primitives/effects/fade'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { PageTitle } from '@/components/PageTitle'

export const Route = createFileRoute('/_app/profile/')({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    const { isAuthenticated, isLoading } = context.auth
    if (!isAuthenticated && !isLoading) {
      throw redirect({ to: '/login' })
    }
  },
})

function ProfileSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-6">
        <Skeleton className="h-24 w-24 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <Skeleton className="h-px w-full" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    </div>
  )
}

function RouteComponent() {
  const {
    adminUser,
    keycloakId,
    databaseId,
    isLoading,
    // Avatar
    fileInputRef,
    isPictureLoading,
    cropDialogOpen,
    imageToCrop,
    handleFileChange,
    handleCropComplete,
    handleCropClose,
    handleDeletePicture,
  } = useProfilePage()

  if (isLoading || !adminUser || !keycloakId || !databaseId) {
    return (
      <div className="py-4">
        <ProfileSkeleton />
      </div>
    )
  }

  return (
    <div className="py-4">
      <PageTitle title="Profile" />
      <Fade inView inViewOnce>
        <ProfileHero
          adminUser={adminUser}
          isPictureLoading={isPictureLoading}
          fileInputRef={fileInputRef}
          onFileChange={handleFileChange}
          onDeletePicture={handleDeletePicture}
        />
      </Fade>

      <Separator className="my-6" />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <Fade inView inViewOnce delay={60}>
            <PersonalInfoForm adminUser={adminUser} databaseId={databaseId} />
          </Fade>
        </div>

        <div className="space-y-6">
          <Fade inView inViewOnce delay={120}>
            <UpdatePassword keycloakId={keycloakId} email={adminUser.email} />
          </Fade>

          <Fade inView inViewOnce delay={180}>
            <BrowserSessions keycloakId={keycloakId} />
          </Fade>
        </div>
      </div>

      {imageToCrop && (
        <ImageCropDialog
          open={cropDialogOpen}
          imageSrc={imageToCrop}
          onClose={handleCropClose}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  )
}
